'use no memo';

import {Action, Card} from 'platform/components';
import {Box, Grid, GridItem, Hide, Show} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetBusinessCaseQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {CommentsWidget, usePermissions} from '@dms/shared';

import {buildArray, useRequiredParams} from 'shared/web';

import {BusinessCasePurchaseVehicleBrokerageFeesDetail} from './BusinessCasePurchaseVehicleBrokerageFeesDetail';
import {BusinessCasePurchaseVehicleBrokerageFeesForm} from './BusinessCasePurchaseVehicleBrokerageFeesForm';

const BrokerageFeesTab = () => {
  const {id: businessCaseId} = useRequiredParams();
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});
  const offer = businessCase?.offers[0];
  const isEditable = businessCase?.actions.purchaseEditVehicle;
  const purchaseVehicle = businessCase?.offers?.[0]?.purchaseBrokerageVehicles?.[0];
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: purchaseVehicle?.vehicleId ?? ''},
    {skip: isNil(purchaseVehicle?.vehicleId)}
  );
  const isConfirmed = !!purchaseVehicle?.brokerageFees;

  const [isDetail, setDetail] = useState((isConfirmed && isEditable) || !isEditable);

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: purchaseVehicle?.vehicleId ?? '',
    },
    {skip: isNil(purchaseVehicle?.vehicleId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.businessCase,
    recordId: businessCaseId,
  });

  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
    hasEditBusinessCasePurchaseBrokerageFeesPermission,
  ] = usePermissions({
    permissionKeys: [
      'vehicleCommentRead',
      'vehicleCommentAdd',
      'vehicleCommentDelete',
      'editBusinessCasePurchaseBrokerageFees',
    ],
    scopes: {
      vehicleCommentAdd: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleCommentRead: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      vehicleCommentDelete: {
        participation: vehicleParticipation,
        branchId: vehicleData?.branchId,
      },
      editBusinessCasePurchaseBrokerageFees: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const toggleDetail = () => setDetail((prevDetail) => !prevDetail);

  useEffect(
    function syncDetailView() {
      setDetail((isConfirmed && isEditable) || !isEditable);
    },
    [isConfirmed, isEditable]
  );

  const cardTitle = isDetail ? i18n.t('entity.businessCase.labels.contractLength') : undefined;

  return (
    <Grid columns={[1, 2]}>
      <GridItem>
        <Card
          title={cardTitle}
          actions={buildArray<Action>().when(isDetail && isEditable, {
            type: 'button',
            variant: 'link',
            leftIcon: 'image/edit',
            onClick: toggleDetail,
            isDisabled: !hasEditBusinessCasePurchaseBrokerageFeesPermission,
            title: i18n.t('general.actions.edit'),
            'data-testid': testIds.businessCase.buying('brokerageFees-edit'),
          })}
          data-testid={testIds.businessCase.buying('brokerageFees')}
          variant="inlineWhite"
        >
          {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
          {purchaseVehicle && (
            <>
              {isDetail ? (
                <BusinessCasePurchaseVehicleBrokerageFeesDetail
                  values={purchaseVehicle.brokerageFees}
                />
              ) : (
                <BusinessCasePurchaseVehicleBrokerageFeesForm
                  businessCaseId={businessCaseId}
                  offerId={offer?.id}
                  purchaseVehicleId={purchaseVehicle.id}
                  data-testid={testIds.businessCase.buying('brokerageFees-edit')}
                  initialValues={purchaseVehicle}
                  onClose={toggleDetail}
                  isEditDisabled={!hasEditBusinessCasePurchaseBrokerageFeesPermission}
                />
              )}
            </>
          )}
        </Card>
      </GridItem>
      <Hide when={isNilOrEmpty(purchaseVehicle?.vehicleId) || !hasVehicleCommentReadPermission}>
        <Box>
          <Show when={purchaseVehicle}>
            {(purchaseVehicle) => (
              <CommentsWidget
                cardVariant="inlineWhite"
                title={i18n.t('entity.vehicle.labels.vehicleComments')}
                resourceId={EntityResourceIds.vehicle}
                recordId={purchaseVehicle.vehicleId!}
                isAddDisabled={!hasVehicleCommentAddPermission}
                isDeleteDisabled={!hasVehicleCommentDeletePermission}
              />
            )}
          </Show>
        </Box>
      </Hide>
    </Grid>
  );
};

export const BusinessCasePurchaseVehicleBrokerageFees = BrokerageFeesTab;
